import {bbox, booleanIntersects} from '@turf/turf';
import * as geojson from 'geojson';

import * as geoJsonUtils from 'app/utils/geoJsonUtils';

import {ArrowDirection, CellId, GeoTransform, GridSpec, Size} from './types';

const PRESETS: Record<Size, number> = {coarse: 4, medium: 8, fine: 16};

export function applyTransform(
  t: GeoTransform,
  col: number,
  row: number
): geojson.Position {
  return [
    t.originLng + col * t.cellWidthDeg + row * t.rowRotation,
    t.originLat + col * t.colRotation + row * t.cellHeightDeg,
  ];
}

export function defaultSpecFromGeometry(geometry: geojson.Geometry, size: Size): GridSpec {
  const [west, south, east, north] = bbox(geometry);
  const minDim = PRESETS[size];

  // Cosine-correct the longitude axis so cells render square on a Web Mercator
  // map. 1° of latitude is ~constant, but 1° of longitude scales with
  // cos(lat), so without this correction cells look taller than wide at any
  // non-equatorial latitude. We project the bbox into pseudo-equal-area units
  // (degrees-of-latitude-equivalent), pick the cell size there, then convert
  // the longitude step back to degrees by dividing by lonScale.
  const meanLat = (south + north) / 2;
  const lonScale = Math.cos((meanLat * Math.PI) / 180) || 1;

  const widthDeg = east - west;
  const heightDeg = north - south;
  const widthEqualized = widthDeg * lonScale;
  const heightEqualized = heightDeg;

  const cellSizeEqualized = Math.min(widthEqualized, heightEqualized) / minDim;
  const cellSizeLng = cellSizeEqualized / lonScale;
  const cellSizeLat = cellSizeEqualized;

  const cols = Math.max(1, Math.ceil(widthDeg / cellSizeLng));
  const rows = Math.max(1, Math.ceil(heightDeg / cellSizeLat));

  // Origin is the north-west corner of the grid; cellHeightDeg is negative so
  // each row step decreases latitude.
  const transform: GeoTransform = {
    originLng: west,
    originLat: north,
    cellWidthDeg: cellSizeLng, // cosine-corrected so cells render square
    cellHeightDeg: -cellSizeLat,
    rowRotation: 0,
    colRotation: 0,
  };

  return {transform, cols, rows};
}

export type GridFeatureProperties = {col: number; row: number};
export type GridFeatureCollection = geojson.FeatureCollection<
  geojson.Polygon,
  GridFeatureProperties
>;

export function gridSpecToFeatureCollection(spec: GridSpec): GridFeatureCollection {
  const features: geojson.Feature<geojson.Polygon, GridFeatureProperties>[] = [];

  for (let row = 0; row < spec.rows; row++) {
    for (let col = 0; col < spec.cols; col++) {
      const topLeft = applyTransform(spec.transform, col, row);
      const topRight = applyTransform(spec.transform, col + 1, row);
      const bottomRight = applyTransform(spec.transform, col + 1, row + 1);
      const bottomLeft = applyTransform(spec.transform, col, row + 1);

      features.push(
        geoJsonUtils.polygon(
          [[topLeft, topRight, bottomRight, bottomLeft, topLeft]],
          {col, row},
          {id: row * spec.cols + col}
        ) as geojson.Feature<geojson.Polygon, GridFeatureProperties>
      );
    }
  }

  return geoJsonUtils.featureCollection(features);
}

/**
 * Drops cells that don't intersect the given property polygon. Cells that
 * overlap even partially are kept whole; we don't clip them to the property
 * boundary, so cells along the edge will extend slightly outside.
 *
 * Numeric feature ids are preserved (the kept set is a subset of the original
 * ids), so feature-state operations remain stable across filtering.
 */
export function filterCellsToProperty(
  cells: GridFeatureCollection,
  propertyGeometry: geojson.Geometry
): GridFeatureCollection {
  const kept = cells.features.filter((cell) => booleanIntersects(cell, propertyGeometry));
  return geoJsonUtils.featureCollection(kept);
}

export function cellIdToColRow(id: CellId): [number, number] {
  const [colStr, rowStr] = id.split('_');
  return [Number(colStr), Number(rowStr)];
}

export function colRowToCellId(col: number, row: number): CellId {
  return `${col}_${row}`;
}

export function cellIdToFeatureId(id: CellId, cols: number): number {
  const [col, row] = cellIdToColRow(id);
  return row * cols + col;
}

/** Returns [west, south, east, north] for a single cell. */
export function cellBbox(spec: GridSpec, id: CellId): [number, number, number, number] {
  const [col, row] = cellIdToColRow(id);
  const topLeft = applyTransform(spec.transform, col, row);
  const bottomRight = applyTransform(spec.transform, col + 1, row + 1);
  return [
    Math.min(topLeft[0], bottomRight[0]),
    Math.min(topLeft[1], bottomRight[1]),
    Math.max(topLeft[0], bottomRight[0]),
    Math.max(topLeft[1], bottomRight[1]),
  ];
}

/**
 * Steps a single cell in the given arrow direction. When the step would cross
 * the grid edge, carries over to the next row (Right/Left) or next column
 * (Down/Up), wrapping at the very last cell back around to the first.
 *
 * Right at end of row → start of next row.
 * Down at bottom of column → top of next column.
 * Left and Up are the inverses.
 */
function stepOnce(
  col: number,
  row: number,
  arrow: ArrowDirection,
  cols: number,
  rows: number
): [number, number] {
  switch (arrow) {
    case 'right': {
      const nextCol = col + 1;
      return nextCol < cols ? [nextCol, row] : [0, (row + 1) % rows];
    }
    case 'left': {
      const nextCol = col - 1;
      return nextCol >= 0 ? [nextCol, row] : [cols - 1, (row - 1 + rows) % rows];
    }
    case 'down': {
      const nextRow = row + 1;
      return nextRow < rows ? [col, nextRow] : [(col + 1) % cols, 0];
    }
    case 'up': {
      const nextRow = row - 1;
      return nextRow >= 0 ? [col, nextRow] : [(col - 1 + cols) % cols, rows - 1];
    }
  }
}

/**
 * Walks one cell at a time from `(col, row)` in the given arrow direction,
 * carrying across rows or columns at the grid edge (see `stepOnce`). Skips
 * cells absent from `existingCellIds` (cells that were filtered out by the
 * property-intersection step) and returns the first id it lands on. Always
 * steps at least once; never returns the starting cell unless the search
 * wraps all the way around to it. Returns null only when `existingCellIds`
 * is empty.
 */
export function findNextExistingCell(
  col: number,
  row: number,
  arrow: ArrowDirection,
  cols: number,
  rows: number,
  existingCellIds: Set<CellId>
): CellId | null {
  const totalCells = cols * rows;
  let currentCol = col;
  let currentRow = row;
  for (let attempt = 0; attempt < totalCells; attempt++) {
    [currentCol, currentRow] = stepOnce(currentCol, currentRow, arrow, cols, rows);
    const id = colRowToCellId(currentCol, currentRow);
    if (existingCellIds.has(id)) return id;
  }
  return null;
}
